import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { CommonInputProps, InputHelperText, useInput } from "react-admin";
import { TimeOfDay, TimeOfDaySchema, timeOfDayToDateTime } from "../model/TimeOfDay";
import { Weekday, WeekdayEnum } from "../model/Weekday";
import { DateTime } from "luxon";
import { NumberInput } from "./NumberInput";
import { z } from "zod";
import { Fragment } from "react";

type _Line = [DateTime | undefined, DateTime | undefined, number];
type _Lines = Array<_Line>;
type _FieldValue = Record<Weekday, _Lines>;
const _WeeklyHoursInputValue = z.array(
  z.tuple([
    WeekdayEnum,
    /* from: */ TimeOfDaySchema.nullish(),
    /* to: */ TimeOfDaySchema.nullish(),
    /* ue: */ z.number().int().nullish(),
  ]),
);
type _WeeklyHoursInputValue = z.infer<typeof _WeeklyHoursInputValue>;

export function LessonsScheduleInput(props: Omit<CommonInputProps, "parse" | "format" | "label" | "isRequired">) {
  const {
    field,
    fieldState: { error, invalid, isTouched },
    formState: { isSubmitted },
  } = useInput<_WeeklyHoursInputValue>({
    ...props,
    parse: (val: _FieldValue) => {
      const weeklyHours: _WeeklyHoursInputValue = [];
      for (const [weekday, lines] of Object.entries(val)) {
        for (const [from, to, ue] of lines) {
          weeklyHours.push([weekday as Weekday, _dateTime2TimeOfDay(from), _dateTime2TimeOfDay(to), ue]);
        }
      }
      return weeklyHours;
    },
    format: (weeklyHours: undefined | _WeeklyHoursInputValue) => {
      const val: _FieldValue = {
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
        Sunday: [],
      };
      if (weeklyHours) {
        for (const [weekday, from, to] of weeklyHours) {
          const from_ = from ? timeOfDayToDateTime(from) : undefined;
          const to_ = to ? timeOfDayToDateTime(to) : undefined;
          val[weekday].push([from_, to_, _ue(from_, to_)]);
        }
      }
      return val;
    },
    validate: [
      (weeklyHours: undefined | _WeeklyHoursInputValue) => {
        const safeParseResult = _WeeklyHoursInputValue.safeParse(weeklyHours ?? []);
        if (!safeParseResult.success) {
          console.error(`Failed to parse weeklyHours: ${JSON.stringify(weeklyHours, null, 2)}`, safeParseResult.error);
          return "Ungültige Eingabe.";
        }
        const { data } = safeParseResult;
        if (data.length === 0) {
          return "Bitte gib mindestens eine Unterrichtseinheit ein.";
        }
        if (data.some(([_, from, to]) => !(from && to))) {
          return "Bitte vervollständige deine Eingabe.";
        }
        for (const weekday of WeekdayEnum.options) {
          const lines = data.filter(([wd]) => wd === weekday);
          for (let i = 1; i < lines.length; i++) {
            const [, , toPrev] = lines[i - 1];
            const [, fromCurr] = lines[i];
            if (toPrev && fromCurr) {
              const toPrevDT = timeOfDayToDateTime(toPrev);
              const fromCurrDT = timeOfDayToDateTime(fromCurr);
              if (toPrevDT && fromCurrDT && toPrevDT > fromCurrDT) {
                return `Die Unterrichtszeiten am ${_weekday2German[weekday]} überschneiden sich.`;
              }
            }
          }
        }
      },
      ...(Array.isArray(props.validate) ? props.validate : props.validate ? [props.validate] : []),
    ],
  });
  return (
    <div>
      <FormControl sx={{ width: { xs: "100%", sm: "auto" } }}>
        <Table
          sx={{
            display: { xs: "block", sm: "table" },
            width: { xs: "100%", sm: "auto" },
            "& .MuiTableBody-root": { display: { xs: "block", sm: "table-row-group" } },
            "& .MuiTableCell-root": { border: "none", py: 0 },
          }}
        >
          <TableBody>
            {(Object.entries(field.value as _FieldValue) as Array<[Weekday, _Lines]>).map(
              ([weekday, lines], weekdayIndex) => (
                <Fragment key={weekday}>
                  {weekdayIndex > 0 && (
                    <TableRow aria-hidden sx={{ display: { xs: "block", sm: "table-row" }, height: "auto" }}>
                      <TableCell colSpan={5} sx={{ display: { xs: "block", sm: "table-cell" }, p: 0 }}>
                        <Box sx={{ borderBottom: 1, borderColor: "divider", height: "16px" }} />
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    sx={{
                      display: { xs: "block", sm: "table-row" },
                      height: "auto",
                      "& > .MuiTableCell-root": {
                        display: { xs: "block", sm: "table-cell" },
                        pb: 1,
                        pt: weekdayIndex === 0 ? 0 : 2,
                        px: 0,
                      },
                    }}
                  >
                    <TableCell component="th" colSpan={5}>
                      {_weekday2German[weekday]}
                    </TableCell>
                  </TableRow>
                  {lines.length === 0 ? (
                    <_EmptyLessonsScheduleInputLine
                      onAddLine={() => {
                        // eslint-disable-next-line react-compiler/react-compiler
                        field.value[weekday] = [[undefined, undefined, 1]];
                        field.onChange(field.value);
                      }}
                    />
                  ) : (
                    lines.map((line, i) => (
                      <_LessonsScheduleInputLine
                        key={`${weekday}-line-${i}`}
                        value={line}
                        minTime={i === 0 ? undefined : lines[i - 1][1]}
                        onFromChange={(newValue) => {
                          line[0] = newValue;
                          line[1] = newValue?.plus({ minutes: line[2] * 45 });
                          field.onChange(field.value);
                        }}
                        onUEChange={(newValue) => {
                          line[1] = line[0]?.plus({ minutes: newValue * 45 });
                          line[2] = newValue;
                          field.onChange(field.value);
                        }}
                        onRemoveLine={() => {
                          lines.splice(i, 1);
                          field.onChange(field.value);
                        }}
                        onAddLine={
                          _isValid(line)
                            ? () => {
                                lines.splice(i + 1, 0, [undefined, undefined, 1]);
                                field.onChange(field.value);
                              }
                            : undefined
                        }
                      />
                    ))
                  )}
                </Fragment>
              ),
            )}
          </TableBody>
        </Table>
        <FormHelperText error={(isTouched || isSubmitted) && invalid}>
          <InputHelperText touched={isTouched || isSubmitted} error={error?.message} helperText={props.helperText} />
        </FormHelperText>
      </FormControl>
    </div>
  );
}

function _LessonsScheduleInputLine({
  value,
  minTime,
  onFromChange,
  onUEChange,
  onRemoveLine,
  onAddLine,
}: {
  value?: _Line;
  minTime?: DateTime;
  onFromChange: (value: DateTime) => void;
  onUEChange: (value: number) => void;
  onRemoveLine?: () => void;
  onAddLine?: () => void;
}) {
  return (
    <TableRow
      sx={{
        backgroundColor: { xs: "action.hover", sm: "transparent" },
        border: "none",
        borderRadius: { xs: 1, sm: 0 },
        boxSizing: "border-box",
        columnGap: { xs: 0.5, sm: 0 },
        display: { xs: "grid", sm: "table-row" },
        gridTemplateAreas: { xs: '"from separator to" "units actions actions"', sm: "none" },
        gridTemplateColumns: { xs: "minmax(0, 1fr) 12px minmax(0, 1fr)", sm: "none" },
        height: { xs: "auto", sm: "60px" },
        mb: { xs: 1.5, sm: 0 },
        p: { xs: 1, sm: 0 },
        rowGap: { xs: 1, sm: 0 },
      }}
    >
      <TableCell sx={{ gridArea: { xs: "from", sm: "auto" }, p: 0 }}>
        <_TimePicker
          value={value?.[0] ?? null}
          minTime={minTime}
          slotProps={{ textField: { size: "small" } }}
          onChange={
            (value) =>
              onFromChange(value as DateTime) /* ... value is a DateTime because of the AdapterLuxon (see main.tsx) */
          }
        />
      </TableCell>
      <TableCell
        sx={{
          alignItems: "center",
          display: { xs: "flex", sm: "table-cell" },
          gridArea: { xs: "separator", sm: "auto" },
          justifyContent: "center",
          px: { xs: 0, sm: 2 },
        }}
      >
        <Typography>-</Typography>
      </TableCell>
      <TableCell sx={{ gridArea: { xs: "to", sm: "auto" }, p: 0 }}>
        <_TimePicker value={value?.[1] ?? null} disabled slotProps={{ textField: { size: "small" } }} />
      </TableCell>
      <TableCell sx={{ gridArea: { xs: "units", sm: "auto" }, px: { xs: 0, sm: 2 }, pr: { sm: 0 } }}>
        <NumberInput
          initialValue={value?.[2] ?? 1}
          unit="UE"
          min={1}
          max={4}
          size="small"
          onChange={onUEChange}
          sx={{
            minWidth: 0,
            width: { xs: "100%", sm: "95px" },
            "& .MuiInputBase-root": { minWidth: 0 },
            "& fieldset": { width: { xs: "100%", sm: "95px" } },
            "& input": { width: "16px" },
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          alignItems: "center",
          display: { xs: "flex", sm: "table-cell" },
          gridArea: { xs: "actions", sm: "auto" },
          justifyContent: { xs: "flex-end", sm: "initial" },
          p: 0,
          pl: { sm: "5px" },
        }}
      >
        <Tooltip title="Unterrichtsblock entfernen">
          <span>
            <IconButton
              aria-label="Unterrichtsblock entfernen"
              disabled={!onRemoveLine}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                onRemoveLine?.();
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Unterrichtsblock hinzufügen">
          <span>
            <IconButton
              aria-label="Unterrichtsblock hinzufügen"
              disabled={!onAddLine}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                onAddLine?.();
              }}
            >
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function _EmptyLessonsScheduleInputLine(props: { onAddLine: () => void }) {
  const { onAddLine } = props;

  return (
    <TableRow sx={{ display: { xs: "block", sm: "table-row" }, height: "auto", mb: { xs: 1.5, sm: 0 } }}>
      <TableCell colSpan={5} sx={{ display: { xs: "block", sm: "table-cell" }, p: 0 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onAddLine();
          }}
        >
          Unterrichtsblock hinzufügen
        </Button>
      </TableCell>
    </TableRow>
  );
}

const _TimePicker = styled(TimePicker)(({ theme }) => ({
  height: "40px",
  width: "110px",
  "& .MuiInputBase-root": { width: "110px", minWidth: "110px", height: "40px !important" },
  "& input": { paddingLeft: "10px", width: "90px" },
  "& .MuiInputAdornment-root": { ml: 0, mr: "5px" },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    "& .MuiInputBase-root": { width: "100%", minWidth: 0 },
    "& input": { boxSizing: "border-box", minWidth: 0, width: "100%" },
  },
}));

const _weekday2German: Record<Weekday, string> = {
  Monday: "Montag",
  Tuesday: "Dienstag",
  Wednesday: "Mittwoch",
  Thursday: "Donnerstag",
  Friday: "Freitag",
  Saturday: "Samstag",
  Sunday: "Sonntag",
};

function _dateTime2TimeOfDay(dateTime: DateTime | undefined): TimeOfDay | undefined {
  return dateTime?.isValid ? dateTime.toFormat("HH:mm") : undefined;
}

function _isValid(line: _Line): boolean {
  return !!_dateTime2TimeOfDay(line[0]);
}

function _ue(from: DateTime | undefined, to: DateTime | undefined): number {
  if (!(from && to)) {
    return 1;
  }
  return Math.max(1, Math.floor(to.diff(from, "minutes").minutes / 45));
}
